import React, { useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, query, where, Timestamp, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import swal from 'sweetalert';
import { getAuth, onAuthStateChanged } from "firebase/auth"; 
import { ToastContainer, toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { logAdminActivity } from '../utils/activityLogger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import profilePictureContImage from '../Images/defprofile.png';
import { buildFeeReminderEmail } from '../utils/emailTemplates';

const AUTOMATED_REMINDER_TYPES = ['before_5_days', 'due_date', 'after_3_days'];

const toDateSafe = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatReminderDate = (value) => {
  const dateObj = toDateSafe(value);
  if (!dateObj) return '—';
  return `${dateObj.toLocaleDateString()} • ${dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const buildDueDateKey = (value) => {
  const dateObj = toDateSafe(value);
  if (!dateObj) return '';
  return `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
};

const formatReminderTypeLabel = (type) => {
  if (type === 'before_5_days') return '5 days before';
  if (type === 'due_date') return 'Due date';
  if (type === 'after_3_days') return '3 days after';
  if (type === 'manual') return 'Manual reminder';
  return 'Reminder';
};

const getReminderDeliveryLabel = (log) => {
  const emailSuccess = Boolean(log?.delivery?.email?.success);
  const smsSuccess = Boolean(log?.delivery?.sms?.success);

  if (emailSuccess && smsSuccess) return 'Email + SMS';
  if (emailSuccess) return 'Email only';
  if (smsSuccess) return 'SMS only';
  return 'Delivery failed';
};

const getReminderLogTone = (log) => {
  if (!log?.anyChannelSucceeded) return 'failed';
  return log?.reminderType === 'manual' ? 'manual' : 'automatic';
};

const getDaysCounterLabel = (daysToDue) => {
  if (daysToDue === null || daysToDue === undefined || Number.isNaN(daysToDue)) {
    return 'Date unavailable';
  }

  if (daysToDue === 0) return 'Due today';
  if (daysToDue > 0) return `In ${daysToDue} day${daysToDue === 1 ? '' : 's'}`;
  const overdueDays = Math.abs(daysToDue);
  return `${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue`;
};

const getSchedulerTone = (summary) => {
  if (!summary) return 'unknown';
  if (Number(summary.errors) > 0) return 'warning';
  if (Number(summary.remindersSent) > 0) return 'healthy';
  return 'neutral';
};

const matchesReminderCycleFilter = (student, filterCycle) => {
  if (filterCycle === 'All') return true;
  if (filterCycle === 'needs_5_day') {
    return student.daysToDue === 5 && !student.cycleReminderStatuses?.before_5_days;
  }
  if (filterCycle === 'needs_due_date') {
    return student.daysToDue === 0 && !student.cycleReminderStatuses?.due_date;
  }
  if (filterCycle === 'needs_overdue_3') {
    return student.daysToDue === -3 && !student.cycleReminderStatuses?.after_3_days;
  }
  if (filterCycle === 'fully_sent') {
    return AUTOMATED_REMINDER_TYPES.every((type) => student.cycleReminderStatuses?.[type]);
  }
  return true;
};

function FeeRemindersComp() {
  const [studentsWithBalance, setStudentsWithBalance] = useState([]);
  const [reminderLogsByStudent, setReminderLogsByStudent] = useState({});
  const [latestSchedulerRun, setLatestSchedulerRun] = useState(null);
  const [totalPaidThisMonth, setTotalPaidThisMonth] = useState(0);
  const [totalExpectedThisMonth, setTotalExpectedThisMonth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCourse, setFilterCourse] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterCycle, setFilterCycle] = useState('All');
  const lastSearchRef = useRef('');
  const lastCourseRef = useRef(filterCourse);
  const lastStatusRef = useRef(filterStatus);
  const lastCycleRef = useRef(filterCycle);

  //generate user token
  const [token, setToken] = useState();
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        user.getIdToken().then(setToken).catch(err => console.error(err));
      }
    });
    return () => unsubscribe();
  }, []);
  //end


  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        const [courseSnap, usersSnapshot, incomeSnap, reminderLogSnap, schedulerLogSnap] = await Promise.all([
          getDocs(collection(db, 'courses')),
          getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
          getDocs(collection(db, 'Income')),
          getDocs(collection(db, 'FeeReminderLogs')),
          getDocs(collection(db, '_systemLogs'))
        ]);

        const courseMap = {};
        courseSnap.forEach((courseDoc) => {
          const data = courseDoc.data();
          courseMap[data.mcourseName] = {
            price: parseFloat(data.mcoursePrice || 0),
            installment: parseFloat(data.feeInstallments || 0),
          };
        });

        const reminderLogMap = {};
        reminderLogSnap.forEach((logDoc) => {
          const data = logDoc.data();
          const studentId = data.studentId;
          if (!studentId) return;

          if (!reminderLogMap[studentId]) {
            reminderLogMap[studentId] = [];
          }

          reminderLogMap[studentId].push({
            id: logDoc.id,
            ...data,
            createdAtDate: toDateSafe(data.createdAt),
            dueDateDate: toDateSafe(data.dueDate)
          });
        });

        Object.keys(reminderLogMap).forEach((studentId) => {
          reminderLogMap[studentId].sort((first, second) => (
            (second.createdAtDate?.getTime?.() || 0) - (first.createdAtDate?.getTime?.() || 0)
          ));
        });

        setReminderLogsByStudent(reminderLogMap);

        const schedulerRuns = [];
        schedulerLogSnap.forEach((logDoc) => {
          const data = logDoc.data();
          if (data.type !== 'fee_reminders_run') return;
          schedulerRuns.push({
            id: logDoc.id,
            ...data,
            createdAtDate: toDateSafe(data.createdAt)
          });
        });

        schedulerRuns.sort((first, second) => (
          (second.createdAtDate?.getTime?.() || 0) - (first.createdAtDate?.getTime?.() || 0)
        ));

        setLatestSchedulerRun(schedulerRuns[0] || null);

        const allStudents = usersSnapshot.docs.map((userDoc) => userDoc.data());
        let paidThisMonth = 0;
        const incomeByStudent = {};

        incomeSnap.forEach((incomeDoc) => {
          const data = incomeDoc.data();
          const amount = parseFloat(data.incomeAmount || 0);
          const studentId = data.studentId;
          const paymentDate = toDateSafe(data.paymentDate);

          if (!studentId) return;

          if (!incomeByStudent[studentId]) incomeByStudent[studentId] = [];
          incomeByStudent[studentId].push({ amount, date: paymentDate });

          if (
            paymentDate &&
            paymentDate.getMonth() === currentMonth &&
            paymentDate.getFullYear() === currentYear
          ) {
            paidThisMonth += amount;
          }
        });

        setTotalPaidThisMonth(paidThisMonth);

        const students = [];
        let overallExpectedThisMonth = 0;

        for (const user of allStudents) {
          const userId = user.userId;
          const courseName = user.course;
          const admissionDate = toDateSafe(user.admissionDate || user.appDate);

          const course = courseMap[courseName];
          if (!course) continue;

          const coursePrice = course.price;
          const monthlyInstallment = course.installment;
          const incomes = incomeByStudent[userId] || [];

          let totalPaid = 0;
          let studentPaidThisMonth = 0;

          for (const income of incomes) {
            totalPaid += income.amount;

            if (
              income.date &&
              income.date.getMonth() === currentMonth &&
              income.date.getFullYear() === currentYear
            ) {
              studentPaidThisMonth += income.amount;
            }
          }

          const balance = coursePrice - totalPaid;
          const thisMonthInstallment = Math.min(monthlyInstallment, balance);
          const expectedAmountThisMonth = Math.max(thisMonthInstallment - studentPaidThisMonth, 0);

          if (balance > 0 && studentPaidThisMonth < thisMonthInstallment) {
            let dueDate = '';
            if (admissionDate instanceof Date) {
              const due = new Date(admissionDate);
              due.setMonth(due.getMonth() + 1);
              const day = due.getDate();
              const getOrdinal = (n) => {
                const s = ['th', 'st', 'nd', 'rd'];
                const v = n % 100;
                return s[(v - 20) % 10] || s[v] || s[0];
              };
              dueDate = `${day}${getOrdinal(day)}`;
            }

            students.push({
              name: user.bio,
              profilePic: user.profilePic || profilePictureContImage,
              phone: user.phoneNumber,
              email: user.userEmail,
              course: courseName,
              balance,
              monthlyInstallment: thisMonthInstallment,
              paidThisMonth: studentPaidThisMonth,
              expectedAmountThisMonth,
              dueDate,
              userId,
              lastSentReminder: user.lastSentFeeReminder
            });

            overallExpectedThisMonth += expectedAmountThisMonth;
          }
        }

        setStudentsWithBalance(students);
        setTotalExpectedThisMonth(overallExpectedThisMonth);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getDueInfo = (dueDateLabel) => {
    const today = new Date();
    const match = String(dueDateLabel || '').match(/\d+/);
    if (!match) return null;
    const dueDay = parseInt(match[0], 10);
    if (Number.isNaN(dueDay)) return null;
    const dueDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
    const isOverdue = today.getDate() > dueDay;
    const daysToDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const status = isOverdue ? 'Overdue' : daysToDue <= 7 ? 'Due Soon' : 'Upcoming';
    return { dueDate, dueDay, daysToDue, status, isOverdue };
  };

  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timeoutId = setTimeout(() => {
      if (!trimmed || trimmed === lastSearchRef.current) return;
      lastSearchRef.current = trimmed;
      logAdminActivity({
        action: 'Search',
        details: `Fee Reminders search: "${trimmed}"`,
        meta: { page: 'Fee Reminders', query: trimmed }
      });
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    if (filterCourse === lastCourseRef.current) return;
    lastCourseRef.current = filterCourse;
    logAdminActivity({
      action: 'Filter',
      details: `Fee Reminders course filter: ${filterCourse}`,
      meta: { page: 'Fee Reminders', filter: 'course', value: filterCourse }
    });
  }, [filterCourse]);

  useEffect(() => {
    if (filterStatus === lastStatusRef.current) return;
    lastStatusRef.current = filterStatus;
    logAdminActivity({
      action: 'Filter',
      details: `Fee Reminders status filter: ${filterStatus}`,
      meta: { page: 'Fee Reminders', filter: 'status', value: filterStatus }
    });
  }, [filterStatus]);

  useEffect(() => {
    if (filterCycle === lastCycleRef.current) return;
    lastCycleRef.current = filterCycle;
    logAdminActivity({
      action: 'Filter',
      details: `Fee Reminders cycle filter: ${filterCycle}`,
      meta: { page: 'Fee Reminders', filter: 'cycle', value: filterCycle }
    });
  }, [filterCycle]);

  const studentsWithStatus = useMemo(() => {
    return studentsWithBalance.map((student) => {
      const dueInfo = getDueInfo(student.dueDate);
      const reminderLogs = reminderLogsByStudent[student.userId] || [];

      if (!dueInfo) {
        return {
          ...student,
          dueStatus: 'Unknown',
          daysToDue: null,
          isOverdue: false,
          dueDateObj: null,
          dueDateKey: '',
          reminderLogs,
          latestReminderLog: reminderLogs[0] || null,
          cycleReminderStatuses: {}
        };
      }

      const dueDateKey = buildDueDateKey(dueInfo.dueDate);
      const cycleLogs = reminderLogs.filter((log) => (
        String(log.dueDateKey || '') === dueDateKey
      ));
      const cycleReminderStatuses = AUTOMATED_REMINDER_TYPES.reduce((acc, type) => {
        acc[type] = cycleLogs.some((log) => (
          log.reminderType === type && log.anyChannelSucceeded
        ));
        return acc;
      }, {});

      return {
        ...student,
        dueStatus: dueInfo.status,
        daysToDue: dueInfo.daysToDue,
        isOverdue: dueInfo.isOverdue,
        dueDateObj: dueInfo.dueDate,
        dueDateKey,
        reminderLogs,
        latestReminderLog: reminderLogs[0] || null,
        cycleReminderStatuses
      };
    });
  }, [studentsWithBalance, reminderLogsByStudent]);

  const statusCounts = useMemo(() => {
    return studentsWithStatus.reduce(
      (acc, student) => {
        if (student.dueStatus === 'Overdue') acc.overdue += 1;
        if (student.dueStatus === 'Due Soon') acc.dueSoon += 1;
        if (student.dueStatus === 'Upcoming') acc.upcoming += 1;
        if (student.dueStatus === 'Unknown') acc.unknown += 1;
        return acc;
      },
      { overdue: 0, dueSoon: 0, upcoming: 0, unknown: 0 }
    );
  }, [studentsWithStatus]);

  const uniqueCourses = useMemo(() => {
    return Array.from(new Set(studentsWithBalance.map((student) => student.course).filter(Boolean))).sort();
  }, [studentsWithBalance]);

  const reminderReadiness = useMemo(() => {
    return studentsWithStatus.reduce((acc, student) => {
      if (student.daysToDue === 5 && !student.cycleReminderStatuses?.before_5_days) acc.before5 += 1;
      if (student.daysToDue === 0 && !student.cycleReminderStatuses?.due_date) acc.dueToday += 1;
      if (student.daysToDue === -3 && !student.cycleReminderStatuses?.after_3_days) acc.after3 += 1;
      return acc;
    }, { before5: 0, dueToday: 0, after3: 0 });
  }, [studentsWithStatus]);

  const filteredStudents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    return studentsWithStatus.filter((student) => {
      const matchesSearch = !search || [
        student.name,
        student.email,
        student.phone,
        student.course
      ].some((value) => String(value || '').toLowerCase().includes(search));

      const matchesCourse = filterCourse === 'All' || student.course === filterCourse;
      const matchesStatus = filterStatus === 'All' || student.dueStatus === filterStatus;
      const matchesCycle = matchesReminderCycleFilter(student, filterCycle);
      return matchesSearch && matchesCourse && matchesStatus && matchesCycle;
    });
  }, [studentsWithStatus, searchTerm, filterCourse, filterStatus, filterCycle]);

  const formatCurrency = (value) => {
    const numeric = Number(value || 0);
    return `KES ${numeric.toLocaleString()}`;
  };

  const recordManualReminderLog = async ({
    student,
    dueDate,
    emailResult,
    smsResult,
    invoiceNumber,
    invoiceRefId
  }) => {
    const newLogRef = doc(collection(db, 'FeeReminderLogs'));
    const nextLog = {
      id: newLogRef.id,
      studentId: student.userId,
      studentName: student.name || '',
      studentEmail: student.email || '',
      studentPhone: student.phone || '',
      course: student.course || '',
      reminderType: 'manual',
      reminderLabel: 'Manual reminder',
      dueDate: Timestamp.fromDate(dueDate),
      dueDateKey: buildDueDateKey(dueDate),
      invoiceNumber: invoiceNumber || '',
      invoiceId: invoiceRefId || '',
      expectedAmountThisCycle: Number(student.expectedAmountThisMonth || 0),
      totalBalance: Number(student.balance || 0),
      anyChannelSucceeded: Boolean(emailResult?.success || smsResult?.success),
      delivery: {
        email: emailResult,
        sms: smsResult
      },
      createdAt: Timestamp.now(),
      createdAtDate: new Date(),
      dueDateDate: dueDate
    };

    await setDoc(newLogRef, {
      studentId: nextLog.studentId,
      studentName: nextLog.studentName,
      studentEmail: nextLog.studentEmail,
      studentPhone: nextLog.studentPhone,
      course: nextLog.course,
      reminderType: nextLog.reminderType,
      reminderLabel: nextLog.reminderLabel,
      dueDate: nextLog.dueDate,
      dueDateKey: nextLog.dueDateKey,
      invoiceNumber: nextLog.invoiceNumber,
      invoiceId: nextLog.invoiceId,
      expectedAmountThisCycle: nextLog.expectedAmountThisCycle,
      totalBalance: nextLog.totalBalance,
      anyChannelSucceeded: nextLog.anyChannelSucceeded,
      delivery: nextLog.delivery,
      createdAt: nextLog.createdAt
    });

    return nextLog;
  };

  const sendReminder = async (student) => {
    if (!token) {
      swal('Authentication Error', 'You need to be logged in to send reminders.', 'error');
      return;
    }

    // Extract numeric day from "13th", "21st", etc.
    const dueInfo = getDueInfo(student.dueDate);

    // const testdate = "2th"; 
    // const dueDayMatch = testdate.match(/\d+/);  
    if (!dueInfo) {
      console.error("Invalid dueDate format:", student.dueDate);
      swal("Error", `Invalid due date for ${student.name}.`, "error");
      return;
    }

    const dueDate = dueInfo.dueDate;

    // Format date as DD/MM/YYYY
    const formatDate = (date) => {
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    };

    let message = '';
    const finalAmount = Math.max(student.expectedAmountThisMonth || 0, 0);
    if (dueInfo.isOverdue) {
      message = `Hi ${student.name}, your fee payment was due on ${formatDate(dueDate)}. Kindly make your payment as soon as possible.`;
    } else {
      message = `Hi ${student.name}, your fee payment is due on ${formatDate(dueDate)}. Please prepare in advance.`;
    }

    const willSend = await swal({
      title: "Send Reminder?",
      text: `Do you want to send an email and SMS reminder to ${student.name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!willSend) {
      return;
    }

    try {
      const firestoreTimestamp = Timestamp.fromDate(dueDate);
      const invoiceNumber = Math.floor(Math.random() * 7971349);
      const invoiceRef = doc(collection(db, "Invoices"));

      toast.info('Sending reminder...');
      await setDoc(invoiceRef, {
        studentId: student.userId,
        amount: finalAmount,
        dueDate: firestoreTimestamp,
        timeCreated: Timestamp.now(),
        paymentId: invoiceRef.id,
        invoiceNumber
      });

      const emailAddress = student.email;
      const emailSubject = `Invoice #${invoiceNumber}`;
      const emailHtml = buildFeeReminderEmail({
        studentName: student.name,
        amount: finalAmount,
        dueDate,
        invoiceNumber,
        message
      });

      let emailResult = { attempted: false, success: false, skipped: true, reason: 'Missing email address' };
      if (emailAddress) {
        const emailResponse = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-email', {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            email: emailAddress,
            message: emailHtml,
            subject: emailSubject
          })
        });

        const emailPayload = await emailResponse.json().catch(() => ({}));
        emailResult = {
          attempted: true,
          success: emailResponse.ok,
          response: emailPayload,
          error: emailResponse.ok ? '' : (emailPayload.error || 'Email request failed')
        };
      }

      const smsMessage = `${message} View your invoice here https://live.ubunifucollege.com/view-invoice?${invoiceNumber}. Thank you. - Nutrition And Dietetics Academy`;
      let smsResult = { attempted: false, success: false, skipped: true, reason: 'Missing phone number' };
      if (student.phone) {
        const smsResponse = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            phoneNumber: student.phone,
            message: smsMessage
          })
        });

        const smsPayload = await smsResponse.json().catch(() => ({}));
        smsResult = {
          attempted: true,
          success: smsResponse.ok,
          response: smsPayload,
          error: smsResponse.ok ? '' : (smsPayload.error || 'SMS request failed')
        };
      }

      await setDoc(doc(db, 'users', student.userId), {
        lastSentFeeReminder: Timestamp.now(),
      }, { merge: true }).catch((error) => {
        console.error('Failed to update lastSentFeeReminder directly:', error);
      });

      const nextLog = await recordManualReminderLog({
        student,
        dueDate,
        emailResult,
        smsResult,
        invoiceNumber,
        invoiceRefId: invoiceRef.id
      });

      setReminderLogsByStudent((prev) => ({
        ...prev,
        [student.userId]: [nextLog, ...(prev[student.userId] || [])]
      }));
      setStudentsWithBalance((prev) => prev.map((item) => (
        item.userId === student.userId
          ? { ...item, lastSentReminder: Timestamp.now() }
          : item
      )));

      logAdminActivity({
        action: 'Send Fee Reminder',
        details: `Sent fee reminder to ${student.name} (KES ${finalAmount})`,
        meta: {
          page: 'Fee Reminders',
          studentId: student.userId,
          studentName: student.name,
          amount: finalAmount,
          course: student.course || '',
          emailSuccess: emailResult.success,
          smsSuccess: smsResult.success
        }
      });

      if (emailResult.success || smsResult.success) {
        toast.success('Reminder sent successfully');
        swal('Success', `Reminder sent to ${student.name}`, 'success');
      } else {
        toast.error('Reminder failed to send');
        swal('Error', `Email and SMS both failed for ${student.name}.`, 'error');
      }
    } catch (error) {
      console.error("Error sending reminder:", error);
      swal("Error", "Failed to send reminder. Please try again.", "error");
    }
  };


  return (
    <div className="clientsComp applicationsPage feeRemindersPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Finance</p>
          <h4>Fee Reminders</h4>
          <p className="appsSubtext">Track monthly balances and send reminders to students who are due.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Students Due</span>
            <strong>{studentsWithBalance.length}</strong>
          </div>
          <div className="appsStatChip">
            <span>Overdue</span>
            <strong>{statusCounts.overdue}</strong>
          </div>
          <div className="appsStatChip">
            <span>Due Soon</span>
            <strong>{statusCounts.dueSoon}</strong>
          </div>
          <div className={`appsStatChip feeSchedulerChip feeSchedulerChip--${getSchedulerTone(latestSchedulerRun?.summary)}`}>
            <span>Scheduler</span>
            <strong>{latestSchedulerRun ? 'Active' : 'No run log'}</strong>
          </div>
        </div>
      </div>

      <div className="feeSummaryGrid">
        <div className="feeSummaryCard">
          <div>
            <p className="feeSummaryLabel">Total Paid This Month</p>
            <h3 className="feeSummaryValue">{formatCurrency(totalPaidThisMonth)}</h3>
            <p className="feeSummaryMeta">Payments recorded this month</p>
          </div>
        </div>
        <div className="feeSummaryCard feeSummaryCardAccent">
          <div>
            <p className="feeSummaryLabel">Total Expected This Month</p>
            <h3 className="feeSummaryValue">{formatCurrency(totalExpectedThisMonth)}</h3>
            <p className="feeSummaryMeta">Outstanding expected balances</p>
          </div>
        </div>
      </div>

      <div className="feeOverviewGrid">
        <div className="feeOverviewCard">
          <div className="feeOverviewCardHead">
            <div>
              <p className="feeSummaryLabel">Reminder Readiness</p>
              <h5>What needs to go out today</h5>
            </div>
          </div>
          <div className="feeReadinessGrid">
            <div className="feeReadinessItem">
              <span>5 days before</span>
              <strong>{reminderReadiness.before5}</strong>
            </div>
            <div className="feeReadinessItem">
              <span>Due today</span>
              <strong>{reminderReadiness.dueToday}</strong>
            </div>
            <div className="feeReadinessItem">
              <span>3 days overdue</span>
              <strong>{reminderReadiness.after3}</strong>
            </div>
          </div>
        </div>

        <div className={`feeOverviewCard feeOverviewCard--${getSchedulerTone(latestSchedulerRun?.summary)}`}>
          <div className="feeOverviewCardHead">
            <div>
              <p className="feeSummaryLabel">Scheduler Status</p>
              <h5>{latestSchedulerRun ? 'Last automation run' : 'Waiting for first run'}</h5>
            </div>
            <span className={`feeSchedulerStatus feeSchedulerStatus--${getSchedulerTone(latestSchedulerRun?.summary)}`}>
              {latestSchedulerRun
                ? (Number(latestSchedulerRun?.summary?.errors) > 0 ? 'Needs attention' : 'Healthy')
                : 'Unknown'}
            </span>
          </div>
          <div className="feeSchedulerMeta">
            <div>
              <span>Ran at</span>
              <strong>{latestSchedulerRun ? formatReminderDate(latestSchedulerRun.createdAtDate || latestSchedulerRun.createdAt) : 'No run yet'}</strong>
            </div>
            <div>
              <span>Reminders sent</span>
              <strong>{latestSchedulerRun?.summary?.remindersSent ?? 0}</strong>
            </div>
            <div>
              <span>Errors</span>
              <strong>{latestSchedulerRun?.summary?.errors ?? 0}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard">
        <div className="appsSearch input-group">
          <span className="input-group-text">
            <FontAwesomeIcon icon={faSearch} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search student, course, phone, or email"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <div className="appsFilterGroup">
          <select
            className="form-select filterSelect"
            value={filterCourse}
            onChange={(event) => setFilterCourse(event.target.value)}
          >
            <option value="All">All Courses</option>
            {uniqueCourses.map((course) => (
              <option key={course} value={course}>
                {course}
              </option>
            ))}
          </select>
          <select
            className="form-select filterSelect"
            value={filterStatus}
            onChange={(event) => setFilterStatus(event.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Due Soon">Due Soon</option>
            <option value="Overdue">Overdue</option>
            <option value="Upcoming">Upcoming</option>
          </select>
          <select
            className="form-select filterSelect"
            value={filterCycle}
            onChange={(event) => setFilterCycle(event.target.value)}
          >
            <option value="All">All Reminder Cycles</option>
            <option value="needs_5_day">Needs 5-day reminder</option>
            <option value="needs_due_date">Needs due-date reminder</option>
            <option value="needs_overdue_3">Needs 3-day overdue reminder</option>
            <option value="fully_sent">All cycle reminders sent</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="appsTableCard">
          <div className="appsTableWrap">
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="appsTableCard">
          <div className="appsTableHeader">
            <h6>Students with outstanding balances</h6>
            <span>{filteredStudents.length} shown</span>
          </div>
          <div className="appsTableWrap feeCardsWrap">
            {filteredStudents.length === 0 ? (
              <div className="appsEmptyState">No students match the current filters.</div>
            ) : (
              <div className="feeReminderCardsGrid">
                {filteredStudents.map((student) => (
                  <article
                    key={`${student.userId}-${student.dueDateKey || student.dueDate}`}
                    className={`feeReminderCard feeReminderCard--${student.dueStatus?.toLowerCase?.().replace(/\s+/g, '-') || 'unknown'}`}
                  >
                    <div className="feeReminderCardHeader">
                      <div className="feeReminderIdentity">
                        <div className="feeReminderAvatar">
                          <img
                            src={student.profilePic || profilePictureContImage}
                            alt={`${student.name || 'Student'} profile`}
                          />
                        </div>
                        <div>
                          <p className="feeReminderCourse">{student.course || 'Course unavailable'}</p>
                          <h5>{student.name || 'Unnamed student'}</h5>
                          <p className="feeReminderContact">{student.email || 'No email on file'}</p>
                        </div>
                      </div>
                      <div className="feeReminderHeaderMeta">
                        <span
                          className={`feeStatusBadge ${
                            student.dueStatus === 'Overdue'
                              ? 'overdue'
                              : student.dueStatus === 'Due Soon'
                                ? 'due'
                                : student.dueStatus === 'Upcoming'
                                  ? 'upcoming'
                                  : 'unknown'
                          }`}
                        >
                          {student.dueStatus}
                        </span>
                        <span className="feeReminderDueText">Due {student.dueDate || 'Unknown'}</span>
                        <span className={`feeReminderDaysCounter ${
                          student.daysToDue < 0 ? 'feeReminderDaysCounter--overdue' : student.daysToDue === 0 ? 'feeReminderDaysCounter--today' : ''
                        }`}>
                          {getDaysCounterLabel(student.daysToDue)}
                        </span>
                      </div>
                    </div>

                    <div className="feeReminderMetaRow">
                      <span className="feeReminderMetaPill">{student.phone || 'No phone number'}</span>
                      <span className="feeReminderMetaPill">
                        {student.latestReminderLog
                          ? `Last reminder ${formatReminderDate(student.latestReminderLog.createdAtDate || student.latestReminderLog.createdAt)}`
                          : 'No reminders yet'}
                      </span>
                    </div>

                    <div className="feeReminderStatsGrid">
                      <div className="feeReminderStat">
                        <span>Installment due</span>
                        <strong>{formatCurrency(student.monthlyInstallment)}</strong>
                      </div>
                      <div className="feeReminderStat">
                        <span>Paid this month</span>
                        <strong className="feeAmountPaid">{formatCurrency(student.paidThisMonth)}</strong>
                      </div>
                      <div className="feeReminderStat">
                        <span>Expected now</span>
                        <strong className="feeAmountExpected">{formatCurrency(student.expectedAmountThisMonth)}</strong>
                      </div>
                      <div className="feeReminderStat">
                        <span>Total balance</span>
                        <strong className="feeAmountBalance">{formatCurrency(student.balance)}</strong>
                      </div>
                    </div>

                    <div className="feeReminderSection">
                      <p className="feeReminderSectionLabel">Automatic Reminder Cycle</p>
                      <div className="feeReminderTimelineGrid">
                        {AUTOMATED_REMINDER_TYPES.map((type) => (
                          <div
                            key={type}
                            className={`feeReminderTimelineItem ${
                              student.cycleReminderStatuses?.[type] ? 'feeReminderTimelineItem--sent' : ''
                            }`}
                          >
                            <span>{formatReminderTypeLabel(type)}</span>
                            <strong>{student.cycleReminderStatuses?.[type] ? 'Sent' : 'Pending'}</strong>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="feeReminderSection">
                      <div className="feeReminderSectionHead">
                        <p className="feeReminderSectionLabel">Recent Reminder History</p>
                        <span>{student.reminderLogs.length} log{student.reminderLogs.length === 1 ? '' : 's'}</span>
                      </div>
                      {student.reminderLogs.length > 0 ? (
                        <div className="feeReminderHistoryList">
                          {student.reminderLogs.slice(0, 3).map((log) => (
                            <div key={log.id} className="feeReminderHistoryItem">
                              <div>
                                <span className={`feeReminderLogBadge feeReminderLogBadge--${getReminderLogTone(log)}`}>
                                  {formatReminderTypeLabel(log.reminderType)}
                                </span>
                                <strong>{getReminderDeliveryLabel(log)}</strong>
                              </div>
                              <small>{formatReminderDate(log.createdAtDate || log.createdAt)}</small>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="feeMutedText">No reminders logged yet.</p>
                      )}
                    </div>

                    <div className="feeReminderActions">
                      <button
                        className="appsViewBtn feeSendBtn feeSendBtnHidden"
                        onClick={() => sendReminder(student)}
                      >
                        Send Manual Reminder
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Bounce}
      />
    </div>
  );
}

export default FeeRemindersComp;
